import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  usePayments,
  useProcessRefund,
  useBulkDeletePayments,
  usePaymentTrashCount,
  useSettings,
} from '../../services/httpServices/queries';
import { useAppSelector } from '../../redux/store/hooks';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { PageHelp } from '../../components/ui/PageHelp';
import { DatePicker } from '../../components/ui/DatePicker';
import { useDebounce } from '../../hooks/useDebounce';
import { PaymentDetailsModal } from './PaymentDetailsModal';
import { buildPaymentReceiptHtml } from '../../utils/paymentReceipt';
import { openPrintWindow } from '../../utils/printWindow';
import { toast } from 'sonner';
import {
  Loader2,
  AlertCircle,
  DollarSign,
  RotateCcw,
  X,
  Landmark,
  Trash2,
  Eye,
  CalendarDays,
  Filter,
  TrendingUp,
  Receipt,
  Search,
} from 'lucide-react';
import {
  billingHistoryCeiling,
  billingHistoryFloor,
  useBillingHistoryAccess,
} from '../../utils/billingAccess';
import type { Payment } from '../../types/billing';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';

const CURRENCY = '৳';
const limit = 20;

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
};

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Local calendar date as YYYY-MM-DD (avoids the UTC shift of toISOString). */
function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Last calendar day of a `YYYY-MM` month. */
function lastDayOfMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number);
  const day = new Date(year, month, 0).getDate();
  return `${yearMonth}-${String(day).padStart(2, '0')}`;
}

function fmtDateLabel(value: string): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

type PaymentRangePreset = 'all' | 'today' | 'week' | 'month' | 'year';

const RANGE_PRESETS: { key: PaymentRangePreset; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
];

function patientName(payment: any): string {
  const patient = payment?.patient;
  const name = patient ? joinFullName(patient) : '';
  return name || payment?.patientId?.slice(0, 8) || '—';
}

export function BillingPayments() {
  const { user } = useAppSelector((state) => state.auth);
  const { unrestricted, minDate, maxDate } = useBillingHistoryAccess();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 350);
  const search = debouncedSearch.trim();
  const [dateFrom, setDateFrom] = useState(() =>
    unrestricted ? '' : billingHistoryFloor(),
  );
  const [dateTo, setDateTo] = useState(() =>
    unrestricted ? '' : billingHistoryCeiling(),
  );

  const { data: paymentsData, isLoading, error } = usePayments({
    search: search || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    limit,
  });
  const processRefund = useProcessRefund();
  const bulkDeletePayments = useBulkDeletePayments();
  const { data: trashCountData } = usePaymentTrashCount();
  const trashCount = Number(trashCountData?.data?.count ?? 0);

  // Hospital letterhead for the printed receipt, mirroring the invoice print so
  // every financial document shares the same branding.
  const { data: settingsData } = useSettings();
  const hospital = useMemo(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      logo: find('hospital_logo'),
      certified: find('hospital_certified'),
      signature: find('hospital_signature'),
      seal: find('hospital_seal'),
      signatoryName: find('hospital_signatory_name'),
    };
  }, [settingsData]);

  const [refundPayment, setRefundPayment] = useState<any>(null);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [modalError, setModalError] = useState<string | null>(null);

  const [viewing, setViewing] = useState<Payment | null>(null);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const payments = useMemo(() => paymentsData?.data || [], [paymentsData]);
  const meta = paymentsData?.meta;

  // A new search always starts from the first page.
  useEffect(() => {
    setPage(1);
  }, [search]);

  /* ------------------------------------------------------------------ */
  /* Compact date-range filter                                           */
  /* ------------------------------------------------------------------ */

  const visiblePresets = useMemo(
    () => RANGE_PRESETS.filter((preset) => unrestricted || preset.key === 'today' || preset.key === 'week'),
    [unrestricted],
  );

  const applyPreset = (preset: PaymentRangePreset) => {
    const now = new Date();
    let from = '';
    let to = '';

    if (preset === 'today') {
      const today = isoDate(now);
      from = today;
      to = today;
    } else if (preset === 'week') {
      from = billingHistoryFloor(now);
      to = billingHistoryCeiling(now);
    } else if (preset === 'month') {
      const month = isoDate(now).slice(0, 7);
      from = `${month}-01`;
      to = lastDayOfMonth(month);
    } else if (preset === 'year') {
      const year = String(now.getFullYear());
      from = `${year}-01-01`;
      to = `${year}-12-31`;
    }

    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  };

  const isPresetActive = (preset: PaymentRangePreset): boolean => {
    const now = new Date();
    if (preset === 'all') return !dateFrom && !dateTo;
    if (preset === 'today') {
      const today = isoDate(now);
      return dateFrom === today && dateTo === today;
    }
    if (preset === 'week') {
      return dateFrom === billingHistoryFloor(now) && dateTo === billingHistoryCeiling(now);
    }
    if (preset === 'month') {
      const month = isoDate(now).slice(0, 7);
      return dateFrom === `${month}-01` && dateTo === lastDayOfMonth(month);
    }
    const year = String(now.getFullYear());
    return dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`;
  };

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    if (value && dateTo && value > dateTo) setDateTo(value);
    setPage(1);
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    if (value && dateFrom && value < dateFrom) setDateFrom(value);
    setPage(1);
  };

  const clearRange = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const activeRangeLabel = useMemo(() => {
    if (!dateFrom && !dateTo) return 'All time';
    if (dateFrom && dateTo && dateFrom === dateTo) return fmtDateLabel(dateFrom);
    if (dateFrom && dateTo) return `${fmtDateLabel(dateFrom)} → ${fmtDateLabel(dateTo)}`;
    if (dateFrom) return `From ${fmtDateLabel(dateFrom)}`;
    return `Until ${fmtDateLabel(dateTo)}`;
  }, [dateFrom, dateTo]);

  const hasDateFilter = Boolean(dateFrom || dateTo);

  /* ------------------------------------------------------------------ */
  /* Summary figures (filtered range, not just the visible page)         */
  /* ------------------------------------------------------------------ */

  const totalCollections = Number(paymentsData?.stats?.totalAmount) || 0;
  const totalTransactions = meta?.total ?? payments.length;
  const averageCollection = totalTransactions > 0 ? totalCollections / totalTransactions : 0;

  const allSelected = payments.length > 0 && payments.every((p: any) => selectedIds.has(p.id));
  const someSelected = payments.some((p: any) => selectedIds.has(p.id));

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(payments.map((p: any) => p.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [payments]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = payments.length > 0 && payments.every((p: any) => prev.has(p.id));
      return everySelected ? new Set() : new Set(payments.map((p: any) => p.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await bulkDeletePayments.mutateAsync(ids);
      const deletedCount = (res as any)?.data?.deleted?.length ?? ids.length;
      setSelectedIds(new Set());
      toast.success(`Moved ${deletedCount} payment${deletedCount === 1 ? '' : 's'} to trash`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete selected payments');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const openRefund = (payment: any) => {
    setRefundPayment(payment);
    setRefundForm({ amount: String(payment.amount || ''), reason: '' });
    setModalError(null);
  };

  const closeRefund = () => {
    setRefundPayment(null);
    setRefundForm({ amount: '', reason: '' });
    setModalError(null);
  };

  const handleRefund = (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    const amount = parseFloat(refundForm.amount);
    if (!amount || amount <= 0) {
      setModalError('Enter a valid refund amount');
      return;
    }
    if (amount > Number(refundPayment.amount)) {
      setModalError('Refund cannot exceed the original payment amount');
      return;
    }
    processRefund.mutate(
      {
        paymentId: refundPayment.id,
        amount,
        reason: refundForm.reason || undefined,
        approvedBy: user?.id,
      },
      {
        onSuccess: () => closeRefund(),
        onError: (err: any) => setModalError(err?.message || 'Failed to process refund'),
      },
    );
  };

  const handlePrintPayment = (payment: Payment) => {
    const html = buildPaymentReceiptHtml({
      payment,
      hospital,
      printedBy: user?.name,
    });
    if (!openPrintWindow(html)) {
      toast.error('Please allow pop-ups to print the receipt');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="w-6 h-6 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text">Payments</h1>
              <PageHelp path="/billing/payments" />
            </div>
            <p className="text-xs text-text-muted">Collections recorded against invoices</p>
          </div>
        </div>
        <Link
          to="/billing/payments/trash"
          className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
          title="View trashed payments"
        >
          <Trash2 size={16} />
          Trash
          {trashCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[0.625rem] font-semibold leading-none">
              {trashCount > 99 ? '99+' : trashCount}
            </span>
          )}
        </Link>
      </div>

      {/* Search + date-range filters */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-3 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by payment no., patient name / mobile, or invoice no…"
              aria-label="Search payments by payment number, patient, or invoice"
              className="w-full pl-9 pr-9 py-2.5 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/40 transition-shadow"
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            ) : (
              <span className="hidden md:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 text-[0.625rem] font-medium uppercase tracking-wide text-text-muted/70">
                Payment · Patient · Invoice
              </span>
            )}
          </div>
          {search && (
            <span className="inline-flex items-center gap-1.5 self-start sm:self-auto px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium whitespace-nowrap">
              {isLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Search size={12} />
              )}
              {meta?.total ?? 0} result{(meta?.total ?? 0) === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-text-muted">
              <Filter size={13} />
              Period
            </span>
            <div className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-background border border-border p-1">
              {visiblePresets.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyPreset(preset.key)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    isPresetActive(preset.key)
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-muted hover:text-text hover:bg-surface'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 xl:ml-auto">
            <div className="w-40">
              <DatePicker
                value={dateFrom}
                onChange={handleDateFromChange}
                min={minDate || undefined}
                max={maxDate || undefined}
                placeholder="From date"
              />
            </div>
            <span className="text-xs text-text-muted">to</span>
            <div className="w-40">
              <DatePicker
                value={dateTo}
                onChange={handleDateToChange}
                min={minDate || undefined}
                max={maxDate || undefined}
                placeholder="To date"
              />
            </div>
            {hasDateFilter && (
              <button
                type="button"
                onClick={clearRange}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-text-muted hover:text-danger border border-border hover:border-danger/50 rounded-md transition-colors"
              >
                <X size={13} />
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 text-[0.6875rem] text-text-muted">
          <CalendarDays size={12} />
          <span>Showing collections for</span>
          <span className="font-semibold text-text">{activeRangeLabel}</span>
          {search && (
            <>
              <span>· matching</span>
              <span className="font-semibold text-primary">“{search}”</span>
            </>
          )}
          {!unrestricted && <span className="text-text-muted">· your role can view the last 7 days</span>}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative overflow-hidden bg-surface rounded-xl border border-success/30 p-4">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-success/80 via-success/40 to-transparent" />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[0.6875rem] font-semibold text-text-muted uppercase tracking-wide">Total Collections</p>
              <p className="text-2xl font-bold text-success mt-1 tabular-nums">{fmt(totalCollections)}</p>
              <p className="text-[0.6875rem] text-text-muted mt-1 truncate">{activeRangeLabel}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-success/10 text-success shrink-0">
              <Landmark size={18} />
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[0.6875rem] font-semibold text-text-muted uppercase tracking-wide">Transactions</p>
              <p className="text-2xl font-bold text-text mt-1 tabular-nums">{totalTransactions.toLocaleString()}</p>
              <p className="text-[0.6875rem] text-text-muted mt-1">
                {search
                  ? 'Matching search'
                  : hasDateFilter
                    ? 'In selected period'
                    : 'All recorded payments'}
              </p>
            </div>
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
              <Receipt size={18} />
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[0.6875rem] font-semibold text-text-muted uppercase tracking-wide">Average Collection</p>
              <p className="text-2xl font-bold text-text mt-1 tabular-nums">{fmt(averageCollection)}</p>
              <p className="text-[0.6875rem] text-text-muted mt-1">Per transaction</p>
            </div>
            <div className="p-2.5 rounded-xl bg-warning/10 text-warning shrink-0">
              <TrendingUp size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> payment{selectedIds.size === 1 ? '' : 's'} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeletePayments.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeletePayments.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Move to trash
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeletePayments.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Payments Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load payments</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={payments.length === 0}
                      title="Select all"
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Payment #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Invoice</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                      <div className="flex flex-col items-center gap-2">
                        <Search size={22} className="text-text-muted/60" />
                        <span>
                          {search
                            ? `No payments match “${search}”`
                            : 'No payments recorded'}
                        </span>
                        {search && (
                          <button
                            type="button"
                            onClick={() => setSearchInput('')}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  payments.map((payment: any) => (
                    <tr key={payment.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(payment.id)}
                          onChange={() => toggleOne(payment.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text">
                        <button
                          type="button"
                          onClick={() => setViewing(payment)}
                          className="hover:text-primary hover:underline transition-colors"
                          title="View payment details"
                        >
                          {payment.paymentNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-text">
                        <button
                          type="button"
                          onClick={() => setViewing(payment)}
                          className="hover:text-primary hover:underline transition-colors text-left"
                          title="View payment details"
                        >
                          {patientName(payment)}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text-muted">
                        {payment.invoice?.invoiceNumber || '—'}
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${Number(payment.amount) < 0 ? 'text-danger' : 'text-success'}`}>
                        {fmt(payment.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text">
                        {METHOD_LABELS[payment.method] || payment.method}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setViewing(payment)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                          {Number(payment.amount) > 0 && (
                            <button
                              onClick={() => openRefund(payment)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-text-muted hover:text-danger hover:underline transition-colors"
                            >
                              <RotateCcw size={13} />
                              Refund
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="payments"
          />
        </div>
      )}

      {/* Payment details modal */}
      <PaymentDetailsModal
        open={viewing !== null}
        payment={viewing}
        onClose={() => setViewing(null)}
        onPrint={handlePrintPayment}
      />

      {/* Refund Modal */}
      {refundPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">Process Refund</h2>
              <button onClick={closeRefund} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            {modalError && (
              <div className="flex items-start gap-2 p-4 bg-danger/10 border-b border-danger/20 text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleRefund} className="p-4 space-y-4">
              <div className="p-3 bg-background border border-border rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-text-muted">Payment</span>
                  <span className="text-text font-mono">{refundPayment.paymentNumber}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-text-muted">Original Amount</span>
                  <span className="text-text">{fmt(refundPayment.amount)}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Refund Amount *</label>
                <ValidatedNumberInput
                  max={refundPayment.amount}
                  value={refundForm.amount}
                  onChange={(v) => setRefundForm({ ...refundForm, amount: String(v) })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Reason</label>
                <textarea
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                <button type="button" onClick={closeRefund} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processRefund.isPending}
                  className="px-4 py-2 bg-danger text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {processRefund.isPending ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                  Process Refund
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Move ${selectedIds.size} payment${selectedIds.size === 1 ? '' : 's'} to trash?`}
        description="This moves the selected payments to trash, removes their amounts from collections and reports, and rebalances the linked invoices and cashbook. You can restore or permanently delete them later."
        confirmLabel={`Move ${selectedIds.size} payment${selectedIds.size === 1 ? '' : 's'} to trash`}
        loading={bulkDeletePayments.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
